/**
 * SSOButtons — Google Sign-In + Apple Sign-In buttons.
 * Loads both SDKs from CDN (no npm packages needed) and calls
 * oauthLogin() from AuthContext when the provider returns a token.
 *
 * Required env vars (set in .env):
 *   VITE_GOOGLE_CLIENT_ID   — Google OAuth 2.0 Web Client ID
 *   VITE_APPLE_CLIENT_ID    — Apple Services ID (e.g. app.game-day.signin)
 *
 * Either can be omitted — if both are missing the component renders nothing.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { IS_NATIVE } from '../utils/apiBase.js';

// 2026-10-09 — whether the login / register pages show the SSO block (the
// "OR" divider + these buttons) at all. Off inside the native apps: Google
// blocks OAuth in embedded WebViews, and any third-party sign-in on iOS makes
// Sign in with Apple mandatory (App Store guideline 4.8). Off on the web too
// when no client id is configured — the pages used to show a bare "OR".
export const SSO_ENABLED = !IS_NATIVE
  && !!(import.meta.env.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_APPLE_CLIENT_ID);

export default function SSOButtons({ onSuccess, onError }) {
  const { oauthLogin } = useAuth();
  const googleRef = useRef(null);
  const [appleReady, setAppleReady] = useState(false);

  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const APPLE_CLIENT_ID  = import.meta.env.VITE_APPLE_CLIENT_ID;

  // ── Google One Tap ────────────────────────────────────────────────────
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const SCRIPT_ID = 'gsi-script';

    const init = () => {
      if (!window.google?.accounts?.id || !googleRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          try {
            await oauthLogin('google', { idToken: credential });
            onSuccess?.();
          } catch (err) {
            // FIX #18 follow-on: backend now returns 409 with
            // code: 'OAUTH_LINK_REQUIRES_LOGIN' when an existing email/password
            // account already owns this email. Show the actionable message,
            // not a generic "sign-in failed".
            const data = err.response?.data;
            const msg = data?.code === 'OAUTH_LINK_REQUIRES_LOGIN'
              ? data.error
              : (data?.error || 'Google sign-in failed');
            onError?.(msg);
          }
        },
      });
      window.google.accounts.id.renderButton(googleRef.current, {
        theme:  'outline',
        size:   'large',
        width:  googleRef.current.offsetWidth || 380,
        text:   'continue_with',
        shape:  'rectangular',
      });
    };

    if (document.getElementById(SCRIPT_ID)) { init(); return; }
    const s = Object.assign(document.createElement('script'), {
      id: SCRIPT_ID, src: 'https://accounts.google.com/gsi/client',
      async: true, defer: true, onload: init,
    });
    document.head.appendChild(s);
  }, [GOOGLE_CLIENT_ID]);

  // ── Apple Sign-In ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!APPLE_CLIENT_ID) return;
    const SCRIPT_ID = 'apple-auth-script';

    const init = () => {
      window.AppleID?.auth.init({
        clientId:    APPLE_CLIENT_ID,
        scope:       'name email',
        redirectURI: window.location.origin,
        usePopup:    true,
      });
      setAppleReady(true);
    };

    if (document.getElementById(SCRIPT_ID)) { init(); return; }
    const s = Object.assign(document.createElement('script'), {
      id: SCRIPT_ID,
      src: 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
      async: true, defer: true, onload: init,
    });
    document.head.appendChild(s);
  }, [APPLE_CLIENT_ID]);

  const handleApple = async () => {
    try {
      const result = await window.AppleID.auth.signIn();
      await oauthLogin('apple', {
        identityToken: result.authorization.id_token,
        user: result.user || null,
      });
      onSuccess?.();
    } catch (err) {
      if (err?.error === 'popup_closed_by_user') return; // user cancelled — silent
      // FIX #18 follow-on
      const data = err.response?.data;
      const msg = data?.code === 'OAUTH_LINK_REQUIRES_LOGIN'
        ? data.error
        : (data?.error || 'Apple sign-in failed');
      onError?.(msg);
    }
  };

  if (!GOOGLE_CLIENT_ID && !APPLE_CLIENT_ID) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Google renders into this div via the GSI SDK */}
      {GOOGLE_CLIENT_ID && (
        <div ref={googleRef} style={{ width: '100%', minHeight: 44 }} />
      )}
      {/* Apple — custom button (Apple doesn't provide a renderer) */}
      {APPLE_CLIENT_ID && appleReady && (
        <button
          type="button"
          onClick={handleApple}
          style={{
            width: '100%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 10,
            padding: '11px 16px', borderRadius: 8, cursor: 'pointer',
            background: 'var(--text)', color: 'var(--bg)', border: 'none',
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 14,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          {/* Apple logo */}
          <svg width="18" height="18" viewBox="0 0 814 1000" fill="currentColor">
            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105.4-63.5-155.5-127.4C46 470.1 0 332.1 0 200.3 0 51.1 79.2 0 154.2 0c69.9 0 113.2 45.3 163.4 45.3 47.8 0 99.9-50.5 170.1-50.5 21.3 0 160.4 1.9 241.4 118.4zm-224.4-87.2c-25.7-31.7-69.5-69.9-124.1-69.9-54.7 0-95.5 36.5-121.7 67.3-25.4 30.1-53.9 80.1-53.9 143.3 0 9 1.3 18.1 2.6 27.2 6.5.6 14.2 1.3 21.9 1.3 54.7 0 102.9-31.7 133-63.5 34.9-36.5 52.6-86.5 52.6-143.3-.1-9.7-1.4-18.7-10.4-22.4z"/>
          </svg>
          Continue with Apple
        </button>
      )}
    </div>
  );
}
